import { memo, useCallback } from 'react';
import { NavLink } from 'react-router-dom';
import { Container, Link as MuiLink } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { PaddedDivider } from '@client/components/common/PaddedDivider';
import { baseUrl, newsUrl } from '@sda/utils/url';
import { useDispatch, useSelector } from '@client/hooks/redux.hooks';
import { startSignout } from '@client/slices/helpers/session.reducer';
import { getSessionUserAdminStatus, getSessionUserState } from '@client/selectors/helpers/login';
import { getSelectedProfilePath } from '@client/selectors/helpers/profile';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { openCreateProfileModal } from '../../Profiles/CreateProfile';
import { openSignupModal } from '../../Signup';
import { openLoginModal } from '../../Login';

const useStyles = makeStyles()((theme) => ({
  root: {
    marginTop: theme.spacing(4),

    [theme.breakpoints.up('md')]: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),

      '& .MuiDivider-root': {
        marginLeft: theme.spacing(2 * -1),
        marginRight: theme.spacing(2 * -1),
      },
    },
  },
  link: {
    color: theme.palette.text.secondary,
    display: 'block',

    '&.active': {
      color: theme.palette.text.primary,
    },

    '&:hover': {
      color: theme.palette.text.disabled,
    },
  },
  stack: {
    marginTop: theme.spacing(2),
  },
}));

interface LinksProps {
  readonly handleClose: () => void;
}

export const Links = memo<LinksProps>(({ handleClose }) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();

  const isMobile = useIsMobile();
  const loggedInState = useSelector(getSessionUserState);
  const isAdmin = useSelector(getSessionUserAdminStatus);

  const profilePath = useSelector(getSelectedProfilePath);

  const handleProfileButtonClick = useCallback((e) => {
    handleClose();

    if (!profilePath) {
      e.preventDefault();
      e.stopPropagation();

      openCreateProfileModal();
    }
  }, [profilePath, handleClose]);

  const handleSignOut = useCallback(() => {
    handleClose();

    dispatch(startSignout());
  }, [dispatch, handleClose]);

  return (
    <Container
      maxWidth="xs"
      className={classes.root}
    >
      {!loggedInState ? (
        <>
          <MuiLink
            variant={isMobile ? 'h3' : 'subtitle1'}
            underline="none"
            className={classes.link}
            href={newsUrl('/pricing')}
            gutterBottom
            onClick={handleClose}
          >
            Pricing
          </MuiLink>

          <MuiLink
            variant={isMobile ? 'h3' : 'subtitle1'}
            underline="none"
            className={classes.link}
            href={newsUrl('/host')}
            gutterBottom
            onClick={handleClose}
          >
            Host a Show
          </MuiLink>

          <MuiLink
            variant={isMobile ? 'h3' : 'subtitle1'}
            underline="none"
            className={classes.link}
            component="button"
            gutterBottom
            onClick={() => {
              handleClose();
              openSignupModal();
            }}
          >
            Sign Up
          </MuiLink>

          <MuiLink
            variant={isMobile ? 'h3' : 'subtitle1'}
            underline="none"
            className={classes.link}
            component="button"
            onClick={() => {
              handleClose();
              openLoginModal();
            }}
          >
            Log In
          </MuiLink>
        </>
      ) : (
        <>
          {isAdmin && (
            <MuiLink
              variant={isMobile ? 'h3' : 'subtitle1'}
              underline="none"
              component={NavLink}
              to="/admin/shows"
              className={classes.link}
              onClick={handleClose}
              gutterBottom
            >
              Admin
            </MuiLink>
          )}

          <MuiLink
            variant={isMobile ? 'h3' : 'subtitle1'}
            underline="none"
            component={NavLink}
            to={profilePath || '/'}
            className={classes.link}
            onClick={handleProfileButtonClick}
            gutterBottom
          >
            Profile
          </MuiLink>

          <MuiLink
            variant={isMobile ? 'h3' : 'subtitle1'}
            underline="none"
            component={NavLink}
            to="/settings"
            className={classes.link}
            onClick={handleClose}
          >
            Settings
          </MuiLink>

          <PaddedDivider margin="small" />

          <MuiLink
            variant={isMobile ? 'h3' : 'subtitle1'}
            underline="none"
            component={NavLink}
            to="/shows"
            className={classes.link}
            onClick={handleClose}
            gutterBottom
          >
            Upcoming Shows
          </MuiLink>

          <MuiLink
            variant={isMobile ? 'h3' : 'subtitle1'}
            underline="none"
            component={NavLink}
            to="/mytickets"
            className={classes.link}
            onClick={handleClose}
          >
            My Tickets
          </MuiLink>
        </>
      )}

      <PaddedDivider margin="small" />

      <div className={classes.stack}>
        <MuiLink
          variant={isMobile ? 'body1' : 'caption'}
          underline="none"
          component={NavLink}
          to="/about-us"
          target="_blank"
          rel="noopener noreferrer"
          className={classes.link}
          onClick={handleClose}
          paragraph
        >
          About Us
        </MuiLink>

        <MuiLink
          variant={isMobile ? 'body1' : 'caption'}
          underline="none"
          href={baseUrl('/support')}
          target="_blank"
          rel="noopener noreferrer"
          className={classes.link}
          onClick={handleClose}
          paragraph
        >
          Knowledge Base
        </MuiLink>

        <MuiLink
          variant={isMobile ? 'body1' : 'caption'}
          underline="none"
          component={NavLink}
          to="/contact-us"
          className={classes.link}
          onClick={handleClose}
          paragraph
        >
          Contact Support
        </MuiLink>

        {Boolean(loggedInState) && (
          <MuiLink
            id="sda-nav-menu-signout-item"
            underline="none"
            onClick={handleSignOut}
            variant={isMobile ? 'body1' : 'caption'}
            className={classes.link}
            component="button"
          >
            Log Out
          </MuiLink>
        )}
      </div>
    </Container>
  );
});
